/**
 * Staff sign-in code (2FA) for Brix Order's OWN staff sign-ins.
 *
 * The hub (alamedapointbg.com) texts staff a code after they sign in there.
 * A sign-in here — password or Microsoft — is a NEW Supabase session the hub
 * never saw, so its token says mfa:false, and once the database enforces the
 * code (Required mode in ops.mfa_settings, People & Access; apbg-billing
 * 20261102l_mfa_enforce) that session stops counting as staff. So
 * ProtectedRoute runs the gateway's shared card
 * (https://alamedapointbg.com/mfa-gate.js) for a STAFF token that lacks
 * mfa:true, then refreshes the session so the new token carries it.
 *
 * ⚠ CUSTOMERS ARE NEVER ASKED. `needsStaffCode()` only says yes for a token
 * whose role is a staff role (the same rule as ops.fn_mfa_is_staff_user);
 * a customer token never even reaches the gateway. The gateway decides the
 * rest (mode, exempt logins, remembered device, phone) exactly as the hub.
 *
 * The card calls the gateway's /api/mfa cross-origin with the person's own
 * token in the Authorization header (no cookies); the gateway allows
 * orders.brixbev.com and brix-order.netlify.app. "Remember this device" is
 * per web address, so the first code here is a new device even if the hub
 * remembers the browser — accepted.
 */
const GATE_SRC = 'https://alamedapointbg.com/mfa-gate.js';

export const STAFF_ROLES = [
  'superadmin', 'admin', 'finance', 'operations', 'dispatcher', 'production', 'warehouse', 'sales',
];

export function tokenClaims(token: string | null | undefined): Record<string, any> {
  try {
    const p = (String(token).split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const pad = p + '='.repeat((4 - (p.length % 4)) % 4);
    const txt = typeof atob === 'function' ? atob(pad) : Buffer.from(pad, 'base64').toString('binary');
    return JSON.parse(decodeURIComponent(escape(txt)));
  } catch {
    return {};
  }
}

/** True only for a staff token that hasn't passed the code yet. */
export function needsStaffCode(token: string | null | undefined): boolean {
  if (!token) return false;
  const c = tokenClaims(token);
  if (c.mfa === true) return false;
  const um = c.user_metadata || {};
  const am = c.app_metadata || {};
  if (am.mfa_exempt === true) return false;
  const r = String(um.role || '');
  if (STAFF_ROLES.includes(r) || r.startsWith('ops-')) return true;
  return am.role === 'superadmin' || am.role === 'admin';
}

type MfaApi = {
  gate: (o: {
    token: () => string | null | Promise<string | null>;
    refresh: () => Promise<unknown>;
    signOut: () => unknown;
  }) => Promise<boolean>;
};

declare global {
  interface Window {
    APBGMfa?: MfaApi;
  }
}

let loading: Promise<MfaApi | null> | null = null;
function loadGate(): Promise<MfaApi | null> {
  if (window.APBGMfa) return Promise.resolve(window.APBGMfa);
  if (!loading) {
    loading = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = GATE_SRC;
      s.async = true;
      s.onload = () => resolve(window.APBGMfa ?? null);
      s.onerror = () => {
        loading = null;
        resolve(null);
      };
      document.head.appendChild(s);
      window.setTimeout(() => resolve(window.APBGMfa ?? null), 8000);
    });
  }
  return loading;
}

/**
 * Run the card. Resolves true when the app may continue (passed, skipped in
 * Warn mode, not needed, or the card couldn't load — the database is the
 * real gate). In a Required-mode dead end it never resolves; its Sign out
 * calls `signOut`.
 */
export async function askStaffSignInCode(opts: {
  token: () => string | null | Promise<string | null>;
  refresh: () => Promise<unknown>;
  signOut: () => unknown;
}): Promise<boolean> {
  const gate = await loadGate();
  if (!gate) return true;
  try {
    return await gate.gate(opts);
  } catch {
    return true;
  }
}
